/**
 * Version Merge Info Service
 *
 * Tags a specific version-history entry (a git commit, see
 * versionHistoryService.js/gitService.js) as the result of an auto-merge or a
 * manually-resolved sync conflict -- a metadata tag, not a content copy, same
 * pattern as checkpointService.js.
 *
 * Local-first: written to IndexedDB immediately, synced to the server in the
 * background for cross-device durability.
 */

import { v4 as uuid } from 'uuid';
import apiClient from './apiClient';
import screenwriterDB from '../storage/indexedDB';

class VersionMergeInfoService {
  constructor() {
    window.addEventListener('online', () => this.flushPendingMergeInfo());
  }

  /**
   * Tag a commit as auto-merged or manually-resolved. `connectedUsers` is a
   * [{id, name}] snapshot of who was connected at commit time -- best-effort,
   * degrades to [] if that lookup failed rather than blocking the tag.
   */
  async tagMerge(fileId, commitSha, languageCode, mergeType, connectedUsers = []) {
    const mergeInfo = {
      id: uuid(),
      file_id: fileId,
      commit_sha: commitSha,
      language_code: languageCode,
      merge_type: mergeType,
      connected_users: connectedUsers,
      created_at: new Date().toISOString(),
      synced: 0
    };

    await screenwriterDB.addMergeInfo(mergeInfo);
    this.syncMergeInfo(mergeInfo).catch(() => {
      // Non-fatal — will retry via flushPendingMergeInfo
    });

    return mergeInfo;
  }

  /**
   * Get all merge tags for a file (from local storage), optionally scoped
   * to one language.
   */
  async getMergeInfo(fileId, languageCode = null) {
    return screenwriterDB.getMergeInfo(fileId, languageCode);
  }

  async syncMergeInfo(mergeInfo) {
    try {
      const result = await apiClient.post('/api/version-merge-info', {
        fileId: mergeInfo.file_id,
        commitSha: mergeInfo.commit_sha,
        languageCode: mergeInfo.language_code,
        mergeType: mergeInfo.merge_type,
        connectedUsers: mergeInfo.connected_users
      });
      mergeInfo.server_id = result.id;
      await screenwriterDB.addMergeInfo(mergeInfo); // re-put with server_id
      await screenwriterDB.markMergeInfoSynced(mergeInfo.id);
    } catch (error) {
      console.warn('Version merge info sync failed, will retry when online:', error.message);
    }
  }

  async flushPendingMergeInfo() {
    const pending = await screenwriterDB.getMergeInfoNeedingSync().catch(() => []);
    for (const mergeInfo of pending) {
      await this.syncMergeInfo(mergeInfo);
    }
  }
}

const versionMergeInfoService = new VersionMergeInfoService();
export default versionMergeInfoService;
